import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Loader2, Edit, UserPlus, CheckCircle2, Trash2, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Task, TaskStatus, TaskPriority, TaskUpdate } from '@/app/types/task';
import { taskService } from '@/app/services/taskService';
import { TaskForm } from '../components/TaskForm';
import ReassignTaskModal from '../components/ReassignTaskModal';
import NotesPanel from '@/app/components/notes/NotesPanel';
import { useSQLiteSync } from '@/hooks/useSQLiteSync';
import { getItemByIdAsync, getItemPriceAsync, getTaxRateAsync } from '@/lib/indexeddb/database';

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('info');
  const [isEditMode, setIsEditMode] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [isCreatingInvoice, setIsCreatingInvoice] = useState(false);
  const { isInitialized: isIndexedDBReady } = useSQLiteSync();

  const taskId = id ? parseInt(id, 10) : null;

  // Fetch task data
  const { data: task, isLoading: isLoadingTask, error: taskError } = useQuery({
    queryKey: ['task', taskId],
    queryFn: () => taskService.get(taskId!),
    enabled: Boolean(taskId),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: TaskUpdate) => taskService.update(taskId!, data),
    onSuccess: () => {
      toast.success('Task updated successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
      setIsEditMode(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => taskService.delete(taskId!),
    onSuccess: () => {
      toast.success('Task deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      navigate('/tasks');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    },
  });

  // Mark complete mutation
  const markCompleteMutation = useMutation({
    mutationFn: () => taskService.markComplete(taskId!),
    onSuccess: () => {
      toast.success('Task marked as complete');
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to mark task as complete');
    },
  });

  const handleEdit = () => {
    setIsEditMode(true);
    setActiveTab('info');
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this task? This action cannot be undone.')) {
      deleteMutation.mutate();
    }
  };

  const handleMarkComplete = () => {
    if (confirm('Mark this task as complete?')) {
      markCompleteMutation.mutate();
    }
  };

  const handleReassign = () => {
    setShowReassignModal(true);
  };

  const handleReassignSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['task', taskId] });
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
    setShowReassignModal(false);
  };

  const handleBack = () => {
    navigate('/tasks');
  };

  const handleCreateInvoice = async () => {
    if (!task) return;
    setIsCreatingInvoice(true);

    try {
      const partyId = task.parent_entity_type === 'party' ? task.parent_entity_id : undefined;
      let items: any[] = [];
      let totalAmount = 0;

      // Look up item from IndexedDB if task has a related item
      if (task.related_entity_id && task.related_entity_type === 'item' && isIndexedDBReady) {
        try {
          const item = await getItemByIdAsync(task.related_entity_id);
          if (item) {
            const salePrice = await getItemPriceAsync(item.id, 'sale', task.site_id);
            const taxRate = await getTaxRateAsync(item.id);
            const rate = salePrice || 0;

            items = [{
              item_id: item.id,
              description: item.name || '',
              quantity: 1,
              unit_id: item.unit_id || 1,
              rate,
              discount_percent: 0,
              discount_amount: 0,
              tax_rate_id: taxRate?.id || null,
              godown_id: undefined,
              item: { name: item.name, code: item.sku || '' },
              tax_rate: taxRate ? { rate: taxRate.rate } : undefined,
            }];
            totalAmount = rate;
          }
        } catch (error) {
          console.error('[TaskDetail] Failed to look up item:', error);
        }
      }

      navigate('/sales-invoices/new', {
        state: {
          duplicateInvoice: {
            party_id: partyId,
            site_id: task.site_id || undefined,
            reference_number: '',
            narration: `Created from Task: ${task.title}`,
            items,
            transaction_date: new Date(),
            due_date: null,
            voucher_type_id: 1,
            total_amount: totalAmount,
          },
          fromDuplicate: true,
        },
      });
      toast.info('Task data loaded. Review and save the invoice.');
    } catch (error) {
      console.error('[TaskDetail] Failed to create invoice:', error);
      toast.error('Failed to prepare invoice data');
    } finally {
      setIsCreatingInvoice(false);
    }
  };

  if (isLoadingTask) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (taskError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load task: {(taskError as any).message}
        </AlertDescription>
      </Alert>
    );
  }

  if (!task) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Task not found</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to Tasks
          </Button>
          <h1 className="text-xl font-semibold tracking-tight">{task.title}</h1>
          <Badge variant={getStatusVariant(task.status)} className="capitalize">
            {task.status}
          </Badge>
          <Badge variant={getPriorityVariant(task.priority)} className="capitalize">
            {task.priority}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Create Invoice - visible for completed tasks with a party */}
          {task.status === TaskStatus.COMPLETED && task.parent_entity_type === 'party' && task.parent_entity_id && (
            <Button
              variant="default"
              size="sm"
              onClick={handleCreateInvoice}
              disabled={isCreatingInvoice}
            >
              {isCreatingInvoice ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileText className="h-4 w-4 mr-2" />
              )}
              Create Invoice
            </Button>
          )}

          {/* Actions */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Actions</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleEdit}>
                <Edit className="mr-2 h-4 w-4" />
                <span>Edit</span>
              </DropdownMenuItem>

              <DropdownMenuItem onClick={handleReassign}>
                <UserPlus className="mr-2 h-4 w-4" />
                <span>Reassign User</span>
              </DropdownMenuItem>

              {task.status === TaskStatus.OPEN && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleMarkComplete}>
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    <span>Mark as Complete</span>
                  </DropdownMenuItem>
                </>
              )}

              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleDelete} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                <span>Delete</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Main Content with Tabs */}
      <Card>
        <CardContent className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Task Info</TabsTrigger>
              <TabsTrigger value="notes">Comments/Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="space-y-4 mt-6">
              {isEditMode ? (
                <div>
                  <TaskForm
                    mode="edit"
                    defaultValues={task}
                    onSubmit={(data) => updateMutation.mutate(data as TaskUpdate)}
                    isLoading={updateMutation.isPending}
                  />
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      onClick={() => setIsEditMode(false)}
                      disabled={updateMutation.isPending}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <TaskInfoTab task={task} />
              )}
            </TabsContent>

            <TabsContent value="notes" className="space-y-4 mt-6">
              <NotesPanel 
                entityType="task" 
                entityId={task.id}
                title="Task Notes"
                maxHeight="500px"
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Reassign Modal */}
      {task && (
        <ReassignTaskModal
          open={showReassignModal}
          onOpenChange={setShowReassignModal}
          task={task}
          onSuccess={handleReassignSuccess}
        />
      )}
    </div>
  );
}

// Helper functions for badge variants
const getStatusVariant = (status: TaskStatus): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "default";
    case TaskStatus.OPEN:
      return "secondary";
    case TaskStatus.CANCELLED:
      return "destructive";
    default:
      return "outline";
  }
};

const getPriorityVariant = (priority: TaskPriority): "default" | "secondary" | "destructive" | "outline" => {
  switch (priority) {
    case TaskPriority.HIGH:
      return "destructive";
    case TaskPriority.MEDIUM:
      return "default";
    case TaskPriority.LOW:
      return "secondary";
    default:
      return "outline";
  }
};

// Task Info Tab Component
function TaskInfoTab({ task }: { task: Task }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Task Details</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Title</span>
                <span className="font-medium">{task.title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <Badge variant={getStatusVariant(task.status)} className="capitalize">
                  {task.status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Priority</span>
                <Badge variant={getPriorityVariant(task.priority)} className="capitalize">
                  {task.priority}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Category</span>
                <span className="font-medium">{task.task_category || '-'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Assignment & Dates</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Assigned To</span>
                <span className="font-medium">
                  {task.assigned_to?.full_name || task.assigned_to?.username || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Due Date</span>
                <span className="font-medium">
                  {task.due_date ? format(new Date(task.due_date), 'MMM d, yyyy') : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Created</span>
                <span className="font-medium">
                  {format(new Date(task.created_at), 'MMM d, yyyy')}
                </span>
              </div>
              {task.completed_at && (
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Completed</span>
                  <span className="font-medium">
                    {format(new Date(task.completed_at), 'MMM d, yyyy')}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-4">Description</h3>
          <p className="text-sm whitespace-pre-wrap">
            {task.description || 'No description provided'}
          </p>
        </CardContent>
      </Card>

      {task.parent_entity_type && task.parent_entity_id && (
        <Card>
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold mb-4">Related Entity</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Type</span>
                <span className="font-medium capitalize">{task.parent_entity_type}</span>
              </div>
              {task.parent_entity_type === 'party' && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Party</span>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => window.location.href = `/party/detail/${task.parent_entity_id}`}
                  >
                    View Party Details
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


